import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { getOrderBySession, getMyDownload } from "@/lib/shop.functions";
import { track } from "@/lib/analytics";

export const Route = createFileRoute("/checkout/return")({
  head: () => ({
    meta: [
      { title: "Thank you · Buddha KA" },
      { name: "robots", content: "noindex" },
    ],
  }),
  validateSearch: (search: Record<string, unknown>): { session_id?: string } => ({
    session_id: typeof search.session_id === "string" ? search.session_id : undefined,
  }),
  component: CheckoutReturn,
});

type Order = {
  id: string;
  order_number: string | null;
  product_slug: string;
  product_title: string;
  status: string;
  amount_cents: number;
  currency: string;
  fulfillment_status: string;
  shipping_address: unknown;
  reward_points_awarded: number;
} | null;

function CheckoutReturn() {
  const { session_id } = Route.useSearch();
  const [order, setOrder] = useState<Order>(null);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!session_id) return;
    let cancelled = false;
    (async () => {
      // Webhook is async — poll up to ~15s
      for (let i = 0; i < 10; i++) {
        const row = (await getOrderBySession({ data: { sessionId: session_id } }).catch(() => null)) as Order;
        if (cancelled) return;
        setTries(i + 1);
        if (row) {
          setOrder(row);
          if (row.status === "paid") {
            track("checkout_complete", { slug: row.product_slug, amountCents: row.amount_cents, currency: row.currency });
          }
          return;
        }
        await new Promise((r) => setTimeout(r, 1500));
      }
    })();
    return () => { cancelled = true; };
  }, [session_id]);

  const paid = order?.status === "paid";
  const physical = !!order?.shipping_address;

  return (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <p className="text-xs uppercase tracking-[0.3em] text-gold">
        {paid ? "Received" : "Confirming…"}
      </p>
      <h1 className="mt-4 font-serif text-5xl">
        {paid ? "Thank you for supporting KAfrequency." : "Confirming your payment…"}
      </h1>
      {!order && session_id && (
        <p className="mt-6 text-sm text-muted-foreground">
          Waiting for confirmation from the payment provider ({tries}/10)…
        </p>
      )}
      {paid && (
        <>
          <div className="mt-8 rounded-xl border hairline bg-charcoal p-6 text-left">
            <div className="flex flex-wrap items-start justify-between gap-4 border-b hairline pb-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Order</p>
                <p className="mt-1 font-serif text-2xl text-gold-luminous">{order!.order_number ?? order!.id.slice(0, 8)}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Total paid</p>
                <p className="mt-1 text-lg text-gold-luminous">{formatPrice(order!.amount_cents, order!.currency)}</p>
              </div>
            </div>
            <div className="grid gap-4 py-5 sm:grid-cols-[1fr_auto] sm:items-center">
              <div>
                <p className="font-serif text-xl">{order!.product_title}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  {physical ? "Physical fulfillment queued" : "Digital access unlocked"}
                </p>
              </div>
              {!!order!.reward_points_awarded && (
                <p className="text-sm text-gold-luminous">+{order!.reward_points_awarded} KAfrequency Points</p>
              )}
            </div>
            <p className="border-t hairline pt-4 text-sm text-muted-foreground">
              {physical
                ? "Your order is in the fulfillment queue. Tracking will appear in your account as soon as it is available."
                : "Your product is now in your Library with secure re-download access."}
            </p>
          </div>
          {!physical && <DownloadButton orderId={order!.id} />}
        </>
      )}
      {session_id && (
        <p className="mt-4 text-[11px] uppercase tracking-[0.2em] text-muted-foreground/70">
          Session · {session_id.slice(0, 16)}…
        </p>
      )}
      <div className="mt-10 flex flex-wrap justify-center gap-3">
        <Link to="/account" className="rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian">
          View my orders
        </Link>
        <Link to="/shop" className="rounded-full border hairline px-5 py-2.5 text-sm hover:border-gold">
          Return to shop
        </Link>
      </div>
    </div>
  );
}

function formatPrice(cents: number, currency: string) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format(cents / 100);
}

function DownloadButton({ orderId }: { orderId: string }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <div className="mt-6">
      <button
        type="button"
        disabled={busy}
        onClick={async () => {
          setBusy(true); setErr(null);
          const res = await getMyDownload({ data: { orderId } });
          setBusy(false);
          if ("error" in res) { setErr(res.error); return; }
          window.open(res.url, "_blank", "noopener");
        }}
        className="rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian disabled:opacity-60"
      >
        {busy ? "Preparing…" : "Download now"}
      </button>
      {err && <p className="mt-2 text-xs text-red-400">{err}</p>}
    </div>
  );
}
